import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  EyeIcon,
  ShieldCheckIcon,
  StarIcon,
  CheckBadgeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const CurationPage: React.FC = () => {
  const curatorBenefits = [
    {
      icon: CurrencyDollarIcon,
      title: 'Earn Rewards',
      description: 'Receive a percentage of platform fees from auctions you curate'
    },
    {
      icon: StarIcon,
      title: 'Build Reputation',
      description: 'Successful curation increases your curator score and visibility'
    },
    {
      icon: UserGroupIcon,
      title: 'Community Recognition',
      description: 'Top curators are featured on the platform and earn special badges'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Quality Control',
      description: 'Help maintain platform standards by verifying legitimate listings'
    }
  ];

  const requirements = [
    {
      tier: 'Apprentice Curator',
      stake: '5,000 NDH',
      benefits: ['Review basic listings', '0.1% fee share', 'Curator badge'],
      color: 'from-gray-500 to-gray-700'
    },
    {
      tier: 'Verified Curator',
      stake: '25,000 NDH',
      benefits: ['Review all listing types', '0.25% fee share', 'Priority review queue', 'Curator dashboard'],
      color: 'from-blue-500 to-blue-700'
    },
    {
      tier: 'Expert Curator',
      stake: '100,000 NDH',
      benefits: ['Review & approve high-value items', '0.5% fee share', 'Direct seller contact', 'Expert badge', 'Curation analytics'],
      color: 'from-purple-500 to-purple-700'
    },
    {
      tier: 'Master Curator',
      stake: '500,000 NDH',
      benefits: ['Final approval authority', '1% fee share', 'Curation team leadership', 'Revenue sharing', 'Governance weight bonus'],
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  const curationProcess = [
    {
      step: 1,
      title: 'Submission',
      description: 'Seller submits an item for auction with details, images, and verification documents'
    },
    {
      step: 2,
      title: 'Initial Review',
      description: 'Automated checks verify image authenticity, description quality, and seller history'
    },
    {
      step: 3,
      title: 'Curator Review',
      description: 'Assigned curators evaluate item quality, pricing, and legitimacy'
    },
    {
      step: 4,
      title: 'Approval/Feedback',
      description: 'Item is approved for listing or returned with improvement suggestions'
    },
    {
      step: 5,
      title: 'Quality Badge',
      description: 'Approved items receive verification badges visible to bidders'
    }
  ];

  const stats = [
    { label: 'Active Curators', value: '500+' },
    { label: 'Items Reviewed', value: '50,000+' },
    { label: 'Approval Rate', value: '78%' },
    { label: 'Rewards Distributed', value: '$2.5M+' },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-primary-500/20 rounded-2xl">
                <SparklesIcon className="w-12 h-12 text-primary-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Curation
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Help maintain quality standards on TEMPLUM and earn rewards for your expertise. Curators are the guardians of our marketplace.
            </p>
            <Link
              to="/staking"
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
            >
              Become a Curator
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What is Curation */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-primary-400 font-semibold mb-2 block">Quality Assurance</span>
                <h2 className="text-3xl font-bold text-white mb-6">What is Curation?</h2>
                <p className="text-gray-300 mb-4">
                  Curation is our community-driven quality control system. Curators review auction listings to ensure they meet our standards for authenticity, accuracy, and appropriateness.
                </p>
                <p className="text-gray-300 mb-4">
                  By staking NDH tokens, you can become a curator and earn a share of platform fees from auctions you help verify. The more you stake, the higher your curator tier and earning potential.
                </p>
                <p className="text-gray-300">
                  This decentralized approach ensures fair, unbiased reviews while rewarding community members who contribute to platform quality.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {curatorBenefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={idx} className="bg-dark-800/50 rounded-xl p-4 border border-dark-700">
                      <Icon className="w-8 h-8 text-primary-400 mb-3" />
                      <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                      <p className="text-gray-400 text-sm">{benefit.description}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Curator Tiers */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Curator Tiers</h2>
              <p className="text-gray-400">Stake more NDH to unlock higher tiers and greater rewards</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {requirements.map((tier, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className={`bg-gradient-to-br ${tier.color} rounded-xl p-6`}
                >
                  <h3 className="text-xl font-bold text-white mb-2">{tier.tier}</h3>
                  <p className="text-white/80 text-sm mb-4">Stake: {tier.stake}</p>
                  <ul className="space-y-2">
                    {tier.benefits.map((benefit, bidx) => (
                      <li key={bidx} className="flex items-center gap-2 text-white/90 text-sm">
                        <CheckBadgeIcon className="w-4 h-4 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Curation Process */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Curation Process</h2>
              <p className="text-gray-400">How items are reviewed and approved on TEMPLUM</p>
            </div>
            <div className="space-y-6">
              {curationProcess.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center text-primary-400 font-bold">
                      {step.step}
                    </div>
                    {idx < curationProcess.length - 1 && (
                      <div className="w-0.5 h-full bg-dark-700 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Badges */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Quality Badges</h2>
              <p className="text-gray-400">Items approved by curators receive verification badges</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckBadgeIcon className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Verified</h3>
                <p className="text-gray-400 text-sm">Basic verification by curator review</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <EyeIcon className="w-8 h-8 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Expert Reviewed</h3>
                <p className="text-gray-400 text-sm">In-depth review by expert curator</p>
              </div>
              <div className="bg-dark-800/50 rounded-xl p-6 border border-dark-700 text-center">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <StarIcon className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Premium</h3>
                <p className="text-gray-400 text-sm">Highest level verification with documentation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-primary-500/10 to-purple-500/10 rounded-2xl p-8 md:p-12 border border-primary-500/30 text-center">
              <ChartBarIcon className="w-12 h-12 text-primary-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-white mb-4">
                Ready to Become a Curator?
              </h2>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                Start with the Apprentice tier by staking just 5,000 NDH. Review listings, earn rewards, and help build a trusted marketplace.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/staking"
                  className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl transition-colors"
                >
                  Stake NDH to Start
                </Link>
                <Link
                  to="/docs"
                  className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
                >
                  Read Curator Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CurationPage;
